import { Component, type ErrorInfo, type ReactNode } from 'react';
import { withTranslation, type WithTranslation } from 'react-i18next';
import { Button } from './../../Atoms';

interface Props extends WithTranslation {
    children: ReactNode;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
    state: State = { hasError: false, error: null };

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('[ErrorBoundary]', error, info.componentStack);
    }

    render() {
        const { t } = this.props;
        if (this.state.hasError) {
            return (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '100vh',
                        padding: '2rem 1rem',
                        background: 'var(--color-bg-primary)',
                        color: 'var(--color-text-primary)',
                        fontFamily: 'system-ui, sans-serif',
                        boxSizing: 'border-box',
                    }}
                >
                    <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem', textAlign: 'center' }}>
                        {t('errorBoundary.title')}
                    </h1>
                    <p style={{ opacity: 0.68, marginBottom: '1.5rem', textAlign: 'center' }}>
                        {t('errorBoundary.message')}
                    </p>
                    <pre
                        style={{
                            background: 'var(--color-bg-secondary)',
                            padding: '0.8rem',
                            borderRadius: '8px',
                            maxWidth: '100%',
                            lineHeight: '1',
                            overflow: 'auto',
                            fontSize: '0.8rem',
                            color: 'var(--color-error)',
                            wordBreak: 'break-word',
                        }}
                        className="w-full xl:w-3/4"
                    >
                        {this.state.error?.message}
                    </pre>
                    <Button className="mt-6" onClick={() => window.location.reload()}>
                        {t('errorBoundary.refresh')}
                    </Button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default withTranslation('common')(ErrorBoundary);
